import * as PIXI from 'pixi.js';
import { addPanelsWithoutSnapingToGrid, addPanelsWithSnapingToGrid } from '__editor/panelsEditor/components/panels/panels';
import {
  BLOCK_CREATING_PANEL,
  HIDE_CURSOR,
  LOCK_CURSOR,
  SET_END_POS,
  SET_START_POS,
  RESET_CURSOR_STATE,
  RESET_RUBBERBAND_STATE,
  SHOW_CURSOR,
  SET_TOOLTIP_CONTENT,
  UNBLOCK_CREATING_PANEL,
  UNLOCK_CURSOR,
  UNSET_CURSOR_OVER_PANEL,
  } from 'actions';
import { cursorPosition, isCursorMoving, setRawPointerPosition } from '__editor/panelsEditor/components/cursor/cursor';
import { deployRubberBand, resizeRubberBand, startRubberBand } from '__editor/panelsEditor/components/rubberBand/rubberBand';
import { dispatch, state } from '__common/store';
import {
  endMovingMap,
  isMovingMapInitied,
  MoveMapPoint,
  moveMap,
  startMovingMap,
  } from '__editor/panelsEditor/components/background/moveBackground';
import { getStage } from '__editor/panelsEditor/components/stage/stage';
import { scaleToolInitialized, trunOffScaleTool } from '__editor/panelsEditor/components/scale/scale';
import { panelsInteractionBlock } from '../../../components/roofsSelector/components/roofsSelectorDrawingManager/roofsSelectorDrawingManager';
import { getRenderer } from '../renderer/renderer';
import { advanceRoofSelectingClick, advanceRoofSelectingMove } from '__common/components/advancedSelecting/advancedSelectingEvents';
import { clearActiveArrayShapeSnap } from '__editor/panelsEditor/components/advanceSelecting/arrayShapeSnapping';
import { isULA, products } from '__common/constants/products';
import { measurementToolClick, measurementToolMove } from '__common/components/measurementTool/measurementToolEvents';

function getStageCoordinates(e: MouseEvent): { x: number, y: number } {
  const rect = (e.target as HTMLElement).getBoundingClientRect();
  const screenX = e.clientX - rect.left;
  const screenY = e.clientY - rect.top;
  const stage = getStage();
  const globalPoint = new PIXI.Point(screenX, screenY);
  const localPoint = stage.toLocal(globalPoint);
  return { x: localPoint.x, y: localPoint.y };
}

const noSnap = (point: pixelPoint): pixelPoint => {
  clearActiveArrayShapeSnap();
  return point;
};

const getPointerPosition = (e: MouseEvent | TouchEvent): MoveMapPoint | null => {
  if ('touches' in e && e.touches && e.touches.length) {
    return {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
  }

  if ('changedTouches' in e && e.changedTouches && e.changedTouches.length) {
    return {
      x: e.changedTouches[0].clientX,
      y: e.changedTouches[0].clientY,
    };
  }

  if ('clientX' in e && 'clientY' in e) {
    return {
      x: e.clientX,
      y: e.clientY,
    };
  }

  return null;
};

export function onStageClick(e: MouseEvent) {
  setRawPointerPosition(getStageCoordinates(e));
  const { background: { moveMapMode, moveArrayMode, toggleAttachments }, advanceRoofSelecting, measurementTool, rubberBand: { mode }, projectConfiguration:{ projectEnvConfig:{is_os_project, is_aurora_project} } } = state();
  let is_aurora_os_project = is_aurora_project || is_os_project;
  if (toggleAttachments){
    return;
  }
  if (advanceRoofSelecting.enabled) {
    const renderer = getRenderer();
    const stage = getStage();
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const screenPoint = new PIXI.Point(e.clientX - rect.left, e.clientY - rect.top);
    advanceRoofSelectingClick(renderer, stage, { data: { global: screenPoint } }, noSnap);
  }

  if(measurementTool.enabled && mode === 'disabled') {
    const renderer = getRenderer();
    const stage = getStage();
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const screenPoint = new PIXI.Point(e.clientX - rect.left, e.clientY - rect.top);
    measurementToolClick(renderer, stage, { data: { global: screenPoint } });
  }

  if (panelsInteractionBlock) {
    return;
  }

  if(moveArrayMode) {
    dispatch(HIDE_CURSOR());
    return;
  }

  if(!is_aurora_os_project) dispatch(BLOCK_CREATING_PANEL());

  if (moveMapMode || e.button === ButtonEvent.ScrollClick) {
    dispatch(HIDE_CURSOR());
    return startMovingMap(getPointerPosition(e));
  }

  if(!is_aurora_os_project) dispatch(LOCK_CURSOR());
}

export function onStageMouseMove(e: MouseEvent) {
  const stageCoords = getStageCoordinates(e);
  setRawPointerPosition(stageCoords);
  const { inited, start, mode } = state().rubberBand;
  const { creatingBlocked, desiredTableLength, desiredTableWidth } = state().panels;
  const { scale: { enabled, startPos }, advanceRoofSelecting, measurementTool, background: { toggleAttachments }, projectConfiguration:{ productId  } } = state();
  if(toggleAttachments){
    return;
  }

  if (advanceRoofSelecting.enabled) {
    const renderer = getRenderer();
    const stage = getStage();
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const screenPoint = new PIXI.Point(e.clientX - rect.left, e.clientY - rect.top);
    advanceRoofSelectingMove(renderer, stage, { data: { global: screenPoint } }, noSnap);
  }

  if (measurementTool.enabled && mode === 'disabled') {
    const renderer = getRenderer();
    const stage = getStage();
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const screenPoint = new PIXI.Point(e.clientX - rect.left, e.clientY - rect.top);
    measurementToolMove(renderer, stage, { data: { global: screenPoint } });
  }

  if (isMovingMapInitied()) {
    return moveMap(getPointerPosition(e));
  }

  if (enabled && startPos.x > 0 && startPos.y > 0) {
    return dispatch(SET_END_POS(stageCoords));
  }

  if (creatingBlocked) {
    if (inited && start && isCursorMoving(start.x, start.y, stageCoords.x, stageCoords.y)) {
      resizeRubberBand(start.x, start.y, stageCoords.x, stageCoords.y);
    }

    const { overLapPanel: overLapOnStart } = state().editorCursor;
    if (!inited && !enabled && mode !== 'disabled' && !(isULA(productId) && (!desiredTableLength || !desiredTableWidth))) {
      startRubberBand(cursorPosition().x, cursorPosition().y);
    }
  }
}

export function onStageMouseUp(e: MouseEvent | TouchEvent) {
  const { inited, mode } = state().rubberBand;
  const { creatingBlocked,panelsToBeMoved } = state().panels;
  const { overLapPanel, overPanel } = state().editorCursor;
  const {
    scale: { enabled },
    background: { moveMapMode, moveArrayMode, toggleAttachments, displayManualExposure },
    advanceRoofSelecting,
    measurementTool,
    app,
  } = state();
  const isUbuilderLite = !!(app && app.isUbuilderLite);
  if (panelsInteractionBlock || advanceRoofSelecting.enabled || measurementTool.enabled) {
    return;
  }

  if(toggleAttachments){
    return;
  }

  if (isMovingMapInitied()) {
    const buttons = 'buttons' in e ? e.buttons : ButtonsEvent.NoButton;
    if (buttons !== ButtonsEvent.ScrollHold) {
      endMovingMap();
      // left button is still holded
      if (buttons === ButtonsEvent.LeftHold) {
        return;
      }
      // no button is holded
      dispatch(UNBLOCK_CREATING_PANEL());
      dispatch(SHOW_CURSOR());
      return;
    }
    // scroll is still holded
    dispatch(UNBLOCK_CREATING_PANEL());
    dispatch(SHOW_CURSOR());
  }

  if (enabled) {
    if (!scaleToolInitialized()) {
      return dispatch(SET_START_POS({ x: cursorPosition().x, y: cursorPosition().y }));
    }

    if (scaleToolInitialized()) {
      dispatch(UNBLOCK_CREATING_PANEL());
      return trunOffScaleTool();
    }

    return;
  }


  if (inited && creatingBlocked) {
    deployRubberBand();
  } else if (inited && creatingBlocked && mode === 'draw') {
    dispatch(RESET_RUBBERBAND_STATE());
    dispatch(RESET_CURSOR_STATE());
    dispatch(SET_TOOLTIP_CONTENT(null));
  } else if (!toggleAttachments && !displayManualExposure && !overLapPanel && !overPanel && mode !== 'remove' && !(isUbuilderLite && mode !== 'draw') && mode !== 'exposure' && !(moveArrayMode && panelsToBeMoved.length == 0)) {
    if (shouldSnapToGrid()) {
      addPanelsWithSnapingToGrid();
    } else if (!moveMapMode) {
      addPanelsWithoutSnapingToGrid();
    }
  }


  dispatch(UNBLOCK_CREATING_PANEL());
  if (mode === 'draw') {
    dispatch(UNLOCK_CURSOR());
    dispatch(UNSET_CURSOR_OVER_PANEL());
  }
}

export function onMouseOutside() {
  if (isMovingMapInitied()) {
    dispatch(UNBLOCK_CREATING_PANEL());
    dispatch(SHOW_CURSOR());
    return endMovingMap();
  }
}

const shouldSnapToGrid = (): boolean => {
  const {
    panels: { panels },
    projectConfiguration: { productId },
    background: { moveMapMode, moveArrayMode },
  } = state();
  return panels.length && !moveMapMode && shouldSnapProduct(productId) && !moveArrayMode;
};

const shouldSnapProduct = (productId: number): boolean => {
  return ![products.gft,products.ula, 12].includes(productId);
};


export enum ButtonEvent {
  LeftClick = 0,
  ScrollClick,
  RightClick,
}

export enum ButtonsEvent {
  NoButton = 0,
  LeftHold,
  RightHold,
  ScrollHold = 4,
}
